import { ArrowLeft } from "lucide-react";
import { PortalFooter } from "../components/PortalFooter";
import { appConfig } from "../config/appConfig";

type LegalKind = "privacy" | "terms";

type LegalSection = {
  title: string;
  body?: string[];
  bullets?: string[];
};

const legalPages: Record<LegalKind, { eyebrow: string; title: string; intro: string; sections: LegalSection[] }> = {
  terms: {
    eyebrow: "Terms of Service",
    title: "Companion Pharmacy Prescriber Portal",
    intro:
      "Please read these Terms of Service carefully. They are a binding legal agreement between you and Vets Pets and Companion Pharmacy.",
    sections: [
      {
        title: "1. Acceptance of These Terms",
        body: [
          "These Terms of Service govern your access to and use of the Companion Pharmacy Prescriber Portal and any related services. The Portal is provided by Vets Pets, P.A. and its affiliates, including Companion Pharmacy.",
          "By submitting an access request, registering an account, or using the Portal, you agree to these Terms and to our Privacy Policy. If you do not agree, do not access or use the Portal."
        ]
      },
      {
        title: "2. Eligibility",
        body: ["The Portal is intended exclusively for licensed veterinarians and authorized pharmacy personnel."],
        bullets: [
          "You are at least eighteen (18) years of age.",
          "You hold a current, valid, and unrestricted license to practice veterinary medicine in the state(s) in which you prescribe.",
          "If you prescribe controlled substances, you hold all required federal and state controlled substance registrations.",
          "You have the right to provide all information you submit, including pet owner and patient information, in compliance with applicable law."
        ]
      },
      {
        title: "3. Account Registration and Security",
        body: [
          "Use of the Portal requires that you first submit an access request, which we may approve or reject in our sole discretion. You agree to provide accurate, current, and complete information and to keep it up to date.",
          "You are responsible for maintaining the confidentiality and security of your account credentials and for activity occurring under your account. Notify us immediately if you suspect unauthorized access."
        ]
      },
      {
        title: "4. Electronic Signature and Records Consent",
        body: ["By using the Portal and providing your electronic signature, you agree that:"],
        bullets: [
          "Your electronic signature has the same legal effect as a handwritten signature.",
          "Your signature represents your attestation that submitted information is true and accurate and that prescriptions are issued for a legitimate medical purpose.",
          "You consent to receive Portal records, notices, disclosures, and other communications in electronic form.",
          "You may withdraw consent to electronic records and signatures only by ceasing use of the Portal and notifying us in writing."
        ]
      },
      {
        title: "5. Use of the Portal",
        body: [
          "Subject to your compliance with these Terms, we grant you a limited, non-exclusive, non-transferable, revocable license to access and use the Portal for submitting and managing prescription orders.",
          "You are solely responsible for your clinical decisions and for the accuracy and completeness of patient and pet owner information submitted through the Portal."
        ]
      },
      {
        title: "6. Prohibited Conduct",
        body: ["You agree that you will not:"],
        bullets: [
          "Use the Portal for any unlawful, fraudulent, or harmful purpose.",
          "Submit a prescription without a valid veterinarian-client-patient relationship as required by applicable state law.",
          "Misrepresent your identity, license status, controlled substance registration, or practice affiliation.",
          "Allow another person to submit prescriptions using your account or digital signature.",
          "Reverse engineer, scrape, bypass security features, introduce malware, or attempt unauthorized access."
        ]
      },
      {
        title: "7. Service Availability",
        body: [
          "Availability of medications and Portal functionality may vary based on factors including state licensure, controlled substance classification, and operational requirements.",
          "The Portal relies on third-party services. We may update, modify, suspend, or discontinue all or part of the Portal at any time."
        ]
      },
      {
        title: "8. Disclaimers",
        body: [
          "THE PORTAL AND ALL CONTENT AND SERVICES PROVIDED THROUGH IT ARE PROVIDED “AS IS” AND “AS AVAILABLE,” WITHOUT ANY WARRANTY OF ANY KIND. TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, VETS PETS AND COMPANION PHARMACY DISCLAIM ALL WARRANTIES, EXPRESS, IMPLIED, OR STATUTORY, INCLUDING THE IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, TITLE, AND NON-INFRINGEMENT. THE PORTAL DOES NOT PROVIDE CLINICAL ADVICE OR PROFESSIONAL JUDGMENT, AND ALL CLINICAL DECISIONS ARE YOUR RESPONSIBILITY."
        ]
      },
      {
        title: "9. Limitation of Liability",
        body: [
          "TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, IN NO EVENT WILL VETS PETS, COMPANION PHARMACY, OR THEIR AFFILIATES, OFFICERS, DIRECTORS, EMPLOYEES, AGENTS, LICENSORS, OR SERVICE PROVIDERS BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, EXEMPLARY, OR PUNITIVE DAMAGES, INCLUDING DAMAGES FOR LOST PROFITS, LOST REVENUE, LOSS OF DATA, OR BUSINESS INTERRUPTION, ARISING OUT OF OR RELATING TO THESE TERMS OR THE PORTAL, REGARDLESS OF THE THEORY OF LIABILITY AND EVEN IF WE HAVE BEEN ADVISED OF THE POSSIBILITY OF SUCH DAMAGES. OUR TOTAL CUMULATIVE LIABILITY ARISING OUT OF OR RELATING TO THESE TERMS OR THE PORTAL WILL NOT EXCEED ONE HUNDRED U.S. DOLLARS ($100). Some jurisdictions do not allow the exclusion or limitation of certain damages; to the extent any such exclusion or limitation is not enforceable, our liability is limited to the maximum extent permitted by applicable law."
        ]
      },
      {
        title: "10. Indemnification",
        body: [
          "You agree to defend, indemnify, and hold harmless Vets Pets, Companion Pharmacy, and their respective affiliates, officers, directors, employees, agents, licensors, and service providers from and against any claims, damages, losses, liabilities, costs, and expenses (including reasonable attorneys' fees) arising out of or relating to: (a) your access to or use of the Portal; (b) any prescription you submit through the Portal; (c) your breach of these Terms; (d) your violation of any applicable law, regulation, or third-party right; or (e) any clinical decision or professional judgment you make."
        ]
      },
      {
        title: "11. Intellectual Property",
        body: [
          "The Portal, including all content, software, design, and trademarks, is owned by Vets Pets, Companion Pharmacy, our affiliates, or our licensors, and is protected by intellectual property laws. Except for the limited license granted in Section 5, no rights are transferred or licensed to you by these Terms. You retain ownership of the information you submit, but you grant us a worldwide, royalty-free, non-exclusive license to use, store, reproduce, and process that information as necessary to operate the Portal, fulfill orders, comply with law, and improve our services."
        ]
      },
      {
        title: "12. Suspension and Termination",
        body: [
          "We may suspend or terminate your access to the Portal at any time, with or without cause and with or without notice, including if we believe that you have violated these Terms or that suspension or termination is required by law. You may terminate your account at any time by contacting Companion Pharmacy support. Upon termination, your right to use the Portal ceases immediately. Sections 4, 6, 8, 9, 10, 11, 13, and 14, and any other provisions that by their nature should survive, will survive termination."
        ]
      },
      {
        title: "13. Changes to These Terms",
        body: [
          "We may revise these Terms from time to time. When we do, we will revise the “Last Updated” date above and post the revised Terms in the Portal. Non-material changes take effect when posted, and your continued use of the Portal after that date constitutes acceptance. Material changes, including changes that broaden prescriber obligations, expand our rights, alter dispute resolution, or change limitations of liability or indemnification, will be communicated to registered prescribers by email or in-Portal notice and will become effective only after you have had an opportunity to review them. If you do not agree to a change, you must stop using the Portal."
        ]
      },
      {
        title: "14. Governing Law",
        body: [
          "These Terms and any dispute arising out of or relating to these Terms or the Portal are governed by the laws of the State of North Carolina, without regard to its conflict of laws principles. The state and federal courts located in Wake County, North Carolina have exclusive jurisdiction over any such dispute, and you consent to personal jurisdiction and venue in those courts. Before filing any claim, you agree to attempt to resolve the dispute informally by contacting us using the information in Section 15."
        ]
      },
      {
        title: "15. Miscellaneous and Contact",
        body: [
          "If any provision of these Terms is held unenforceable, the remaining provisions remain in full force. These Terms, together with the Privacy Policy, are the entire agreement between you and us regarding the Portal.",
          "Questions about these Terms or the Portal should be sent to Companion Pharmacy, Attn: Prescriber Portal, 3101 Glenwood Ave #101, Raleigh, NC 27612, Email: support@companionpharmacy.com, Phone: (919) 505-8779."
        ]
      }
    ]
  },
  privacy: {
    eyebrow: "Privacy Policy",
    title: "Companion Pharmacy Prescriber Portal",
    intro:
      "This Privacy Policy describes how Vets Pets, P.A. and its affiliates, including Companion Pharmacy, collect, use, and protect information when you access or use the Portal.",
    sections: [
      {
        title: "1. Introduction",
        body: [
          "Vets Pets, P.A. and its affiliates, including Companion Pharmacy, respect your privacy. By accessing or using the Portal, you acknowledge that you have read and understood this Privacy Policy."
        ]
      },
      {
        title: "2. Scope",
        body: [
          "The Portal is a digital prescribing platform used by licensed veterinarians to submit prescription orders to Companion Pharmacy. Prescribers are responsible for obtaining any consents required from pet owners under applicable law."
        ]
      },
      {
        title: "3. Information We Collect",
        body: ["We collect information you provide through the Portal and information generated automatically when you use it."],
        bullets: [
          "Prescriber information, including name, credentials, license number, state of licensure, email, phone, practice affiliation, account credentials, digital signature, and communications.",
          "Pet owner and patient information, including contact details and clinical details necessary to fill prescriptions.",
          "Prescription information, including drug, dosage, quantity, refills, expiration, and directions for use.",
          "Technical and usage information, cookies, and similar technologies used for functionality, security, and performance."
        ]
      },
      {
        title: "4. Electronic Signature",
        body: [
          "The Portal supports electronic signature functionality for prescription orders. By using the Portal, you consent to the capture and processing of your electronic signature in connection with prescription orders you submit."
        ]
      },
      {
        title: "5. How We Use Information",
        body: ["We use information collected through the Portal to:"],
        bullets: [
          "Create and manage accounts, process prescription-related activities, and support Portal functionality.",
          "Support pharmacy operations and prescription-related workflows.",
          "Communicate with you about your account, orders, security, and operational notices.",
          "Maintain security, integrity, compliance, and lawful operation of the Portal and related services.",
          "Monitor, troubleshoot, and improve the Portal and pharmacy operations."
        ]
      },
      {
        title: "6. How We Share Information",
        body: [
          "We do not sell your personal information, and we do not share your personal information for cross-context behavioral advertising. We share information only as follows: within Vets Pets and Companion Pharmacy; with service providers that support operation of the Portal and related business functions; for compliance with law and protection of rights; in connection with business transfers; and with your direction or consent.",
          "California residents: We do not sell personal information or share it for cross-context behavioral advertising as those terms are defined under the California Consumer Privacy Act."
        ]
      },
      {
        title: "7. Data Retention",
        body: [
          "We retain information for as long as necessary to provide the Portal and to comply with legal, regulatory, recordkeeping, accounting, audit, and reporting obligations."
        ]
      },
      {
        title: "8. How We Protect Information",
        body: [
          "We maintain reasonable administrative, technical, and organizational safeguards designed to protect the Portal and the information it processes. No transmission or storage method is completely secure."
        ]
      },
      {
        title: "9. Your Rights and Choices",
        body: [
          "Through your Portal account, you can review and update profile information. Certain account information may require manual review to maintain prescriber record integrity.",
          "Depending on your state of residence, you may have additional rights with respect to your personal information. To exercise rights under applicable law, contact us using the information below.",
          "You may opt out of non-essential email communications by following unsubscribe instructions. We will continue sending transactional and operational emails relating to your account, orders, and security."
        ]
      },
      {
        title: "10. Children's Privacy",
        body: [
          "The Portal is intended for licensed veterinary professionals and is not directed to children. We do not knowingly collect personal information from individuals under eighteen (18)."
        ]
      },
      {
        title: "11. Changes to This Privacy Policy",
        body: [
          "We may update this Privacy Policy from time to time. When we do, we will revise the “Last Updated” date above. Non-material changes take effect when posted, and your continued use of the Portal after that date constitutes acceptance of the changes. Material changes, including changes that broaden the categories of information we collect or how we share it, will be communicated to registered prescribers by email or in-Portal notice and, where required by applicable law, will become effective only after you have had an opportunity to review them."
        ]
      },
      {
        title: "12. How to Contact Us",
        body: [
          "If you have questions about this Privacy Policy or our privacy practices, contact Companion Pharmacy, Attn: Prescriber Portal, 3101 Glenwood Ave #101, Raleigh, NC 27612, Email: support@companionpharmacy.com, Phone: (919) 505-8779."
        ]
      }
    ]
  }
};

export function LegalPage({ kind, onBack }: { kind: LegalKind; onBack: () => void }) {
  const page = legalPages[kind];

  return (
    <main className="legal-page">
      {import.meta.env.VITE_APP_ENV !== "prod" ? <div className="test-banner">TEST ENVIRONMENT</div> : null}
      <section className="legal-hero">
        <div className="legal-hero-inner">
          <button className="legal-back-link" type="button" onClick={onBack}>
            <ArrowLeft size={16} aria-hidden="true" />
            Back
          </button>
          <p className="eyebrow">{page.eyebrow}</p>
          <h1>{page.title}</h1>
          <p>{page.intro}</p>
          <dl>
            <div>
              <dt>Effective Date</dt>
              <dd>June 2, 2026</dd>
            </div>
            <div>
              <dt>Last Updated</dt>
              <dd>June 2, 2026</dd>
            </div>
          </dl>
        </div>
      </section>

      <article className="legal-content" aria-label={page.eyebrow}>
        {page.sections.map((section) => (
          <section key={section.title}>
            <h2>{section.title}</h2>
            {section.body?.map((paragraph) => <p key={paragraph}>{paragraph}</p>)}
            {section.bullets ? (
              <ul>
                {section.bullets.map((bullet) => <li key={bullet}>{bullet}</li>)}
              </ul>
            ) : null}
          </section>
        ))}
        <section className="legal-contact">
          <h2>Contact</h2>
          <p>
            Email: <a href={`mailto:${appConfig.support.email}`}>{appConfig.support.email}</a>
          </p>
          <p>
            Phone: <a href="tel:+19195058779">(919) 505-8779</a>
          </p>
        </section>
      </article>

      <PortalFooter />
    </main>
  );
}
